import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { FormField } from '@/shared/components/ui/FormField';
import { useApp } from '@/contexts/AppContext';
import { useApiError } from '@/hooks/useApiError';
import { resetPasswordSchema, ResetPasswordFormData, newPasswordSchema, NewPasswordFormData } from '@/types/forms';
import { AuthService } from '@/modules/auth/services';
import { AuthLayout } from '../components/AuthLayout';
import { AuthShell } from '../components/AuthShell';
import { Icon } from '@/shared/icons';

const SUCCESS_TINT = 'color-mix(in srgb, var(--theme-success-color) 14%, transparent)';

export const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const [isPasswordReset, setIsPasswordReset] = React.useState(false);
  const { app } = useApp();
  const { handleApiError } = useApiError();

  // Form: request reset (email)
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    getValues,
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
  });

  // Form: set new password (with token)
  const {
    register: registerNewPw,
    handleSubmit: handleSubmitNewPw,
    formState: { errors: errorsNewPw, isSubmitting: isSubmittingNewPw },
    setError: setErrorNewPw,
  } = useForm<NewPasswordFormData>({
    resolver: zodResolver(newPasswordSchema),
  });

  const onSubmit = async (data: ResetPasswordFormData) => {
    try {
      await AuthService.forgotPassword({ email: data.email });
      setIsSubmitted(true);
      toast.success('E-mail de recuperação enviado!');
    } catch (error) {
      handleApiError(error, setError);
    }
  };

  const onSubmitNewPassword = async (data: NewPasswordFormData) => {
    if (!token) return;
    try {
      await AuthService.resetPassword({
        token,
        password: data.password,
        confirmPassword: data.confirmPassword,
      });
      setIsPasswordReset(true);
      toast.success('Senha redefinida com sucesso!');
    } catch (error) {
      handleApiError(error, setErrorNewPw);
    }
  };

  // Link "Voltar para o login" (estados de formulário 1 e 3)
  const backToLogin = (
    <div className="mt-6 text-center">
      <Link
        to="/login"
        data-testid="reset-back-to-login"
        className="inline-flex items-center gap-1.5 text-sm transition-opacity hover:opacity-80"
        style={{ color: 'var(--theme-muted-text)' }}
      >
        <Icon name="arrow-left" className="h-4 w-4" />
        Voltar para o login
      </Link>
    </div>
  );

  // Determine which panel to render
  let rightPanel: React.ReactNode;

  if (token && isPasswordReset) {
    // === Estado 4: senha redefinida com sucesso ===
    rightPanel = (
      <div className="text-center" data-testid="reset-success">
        <div
          className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full"
          style={{ backgroundColor: SUCCESS_TINT }}
        >
          <Icon name="check" className="h-8 w-8" style={{ color: 'var(--theme-success-color)' }} />
        </div>
        <h1 className="text-2xl font-bold tracking-tight" style={{ color: 'var(--theme-text)' }}>
          Senha redefinida!
        </h1>
        <p className="mx-auto mt-3 max-w-xs text-sm leading-relaxed" style={{ color: 'var(--theme-muted-text)' }}>
          Sua senha foi alterada com sucesso. Você já pode fazer login com a nova senha.
        </p>
        <Link to="/login" className="mt-7 block">
          <Button fullWidth size="lg" icon="arrow-left" data-testid="reset-go-to-login">
            Ir para o login
          </Button>
        </Link>
      </div>
    );
  } else if (token) {
    // === Estado 3: definir nova senha (com token) ===
    rightPanel = (
      <div data-testid="reset-new-password-form">
        <h1 className="text-2xl font-bold tracking-tight" style={{ color: 'var(--theme-text)' }}>
          Definir nova senha
        </h1>
        <p className="mt-1.5 text-sm" style={{ color: 'var(--theme-muted-text)' }}>
          Digite sua nova senha abaixo
        </p>
        <form onSubmit={handleSubmitNewPw(onSubmitNewPassword)} className="mt-7 space-y-4">
          <FormField label="Nova senha" error={errorsNewPw.password?.message} required>
            <Input
              {...registerNewPw('password')}
              type="password"
              autoComplete="new-password"
              placeholder="Mínimo 6 caracteres"
              icon="lock"
              error={errorsNewPw.password?.message}
              data-testid="reset-new-password-input"
            />
          </FormField>
          <FormField label="Confirmar senha" error={errorsNewPw.confirmPassword?.message} required>
            <Input
              {...registerNewPw('confirmPassword')}
              type="password"
              autoComplete="new-password"
              placeholder="Repita a nova senha"
              icon="lock"
              error={errorsNewPw.confirmPassword?.message}
              data-testid="reset-confirm-password-input"
            />
          </FormField>
          <Button type="submit" fullWidth size="lg" loading={isSubmittingNewPw} data-testid="reset-password-submit">
            Redefinir senha
          </Button>
        </form>
        {backToLogin}
      </div>
    );
  } else if (isSubmitted) {
    // === Estado 2: e-mail enviado com sucesso ===
    rightPanel = (
      <div className="text-center" data-testid="reset-email-sent">
        <div
          className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full"
          style={{ backgroundColor: SUCCESS_TINT }}
        >
          <Icon name="check" className="h-8 w-8" style={{ color: 'var(--theme-success-color)' }} />
        </div>
        <h1 className="text-2xl font-bold tracking-tight" style={{ color: 'var(--theme-text)' }}>
          E-mail enviado!
        </h1>
        <p className="mt-3 text-sm leading-relaxed" style={{ color: 'var(--theme-muted-text)' }}>
          Enviamos um link de recuperação para{' '}
          <span className="font-semibold" style={{ color: 'var(--theme-text)' }}>
            {getValues('email')}
          </span>
          . Verifique sua caixa de entrada e siga as instruções para redefinir sua senha.
        </p>
        <Link to="/login" className="mt-7 block">
          <Button variant="secondary" fullWidth size="lg" icon="arrow-left" data-testid="reset-back-to-login">
            Voltar para o login
          </Button>
        </Link>
        <p className="mt-4 text-xs leading-relaxed" style={{ color: 'var(--theme-muted-text)' }}>
          Não recebeu o e-mail? Verifique sua pasta de spam ou{' '}
          <button
            type="button"
            className="font-semibold transition-opacity hover:opacity-80"
            style={{ color: 'var(--theme-primary)' }}
            onClick={() => setIsSubmitted(false)}
            data-testid="reset-try-again"
          >
            tente novamente
          </button>
        </p>
      </div>
    );
  } else {
    // === Estado 1: solicitar reset (e-mail) ===
    rightPanel = (
      <div data-testid="reset-request-form">
        <h1 className="text-2xl font-bold tracking-tight" style={{ color: 'var(--theme-text)' }}>
          Recuperar senha
        </h1>
        <p className="mt-1.5 text-sm" style={{ color: 'var(--theme-muted-text)' }}>
          Digite seu e-mail para receber um link de recuperação
        </p>
        <form onSubmit={handleSubmit(onSubmit)} className="mt-7 space-y-4">
          <FormField label="E-mail" error={errors.email?.message} required>
            <Input
              {...register('email')}
              type="email"
              autoComplete="email"
              placeholder="seu@email.com"
              icon="mail"
              error={errors.email?.message}
              data-testid="reset-email-input"
            />
          </FormField>
          <Button type="submit" fullWidth size="lg" loading={isSubmitting} data-testid="reset-request-submit">
            Enviar link de recuperação
          </Button>
        </form>
        {backToLogin}
      </div>
    );
  }

  return (
    <AuthLayout>
      <AuthShell app={app}>{rightPanel}</AuthShell>
    </AuthLayout>
  );
};
